import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { OnboardingBrand } from '../ai-exam-onboarding-progress.util';
import { AIExamOnboardingShellComponent } from '../AI-exam-onboarding-shell/AI-exam-onboarding-shell.component';

/** Lecteur vidéo (bundle JS précompilé depuis flashlevel-video.jsx). */
const DEMO_HTML_PLAYER = 'assets/ai-exam-onboarding/demo/flashlevel-video-player.html';

type DemoVideoLang = 'fr' | 'en' | 'es' | 'it';

/**
 * Démo d'ergonomie : iframe sur la vidéo procédurale (FR/EN/ES/IT).
 * `brand` (flashlevel | lilate) est transmis à l'iframe pour splash / VO / rail.
 * Peut être montée en preload (cachée) pendant l'écran CGU pour chauffer les voix TTS.
 */
@Component({
    selector: 'lib-ai-exam-onboarding-demo-video',
    standalone: true,
    imports: [CommonModule, TranslateModule, AIExamOnboardingShellComponent],
    templateUrl: './AI-exam-onboarding-demo-video.component.html',
    styleUrls: ['./AI-exam-onboarding-demo-video.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AIExamOnboardingDemoVideoComponent implements OnInit, OnChanges, OnDestroy {
    @Input() brand: OnboardingBrand = 'flashlevel';
    @Input() progressPercent = 88;
    @Input() progressLabel = '';
    /** Affiche l'écran démo (titre, skip, shell). */
    @Input() visible = true;
    /** Iframe montée hors écran pendant le CGU pour précharger les voix. */
    @Input() preload = false;

    @Output() demoFinished = new EventEmitter<void>();

    @ViewChild('demoFrame') demoFrame?: ElementRef<HTMLIFrameElement>;

    /** URL stabilisée : un getter recréait un SafeResourceUrl à chaque CD et rechargeait l'iframe. */
    demoPageUrl!: SafeResourceUrl;
    demoPageTitle = '';

    private langChangeSub?: Subscription;
    private playbackStarted = false;
    /** Invalide les tentatives de démarrage différé (iframe pas encore prête). */
    private startAttemptId = 0;

    constructor(
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.refreshDemoPage();
        this.langChangeSub = this.translate.onLangChange.subscribe(() => {
            this.refreshDemoPage();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['brand'] && !changes['brand'].firstChange) {
            this.refreshDemoPage();
        }
        if (changes['visible']?.currentValue && !this.playbackStarted) {
            this.startDemoPlayback();
        }
        if (changes['visible']?.currentValue === false && changes['visible']?.previousValue === true) {
            this.stopDemoPlayback();
        }
    }

    ngOnDestroy(): void {
        this.stopDemoPlayback();
        this.langChangeSub?.unsubscribe();
    }

    onReady(): void {
        this.stopDemoPlayback();
        this.demoFinished.emit();
    }

    /** Langue alignée sur l'interface (fr / en / es / it). */
    private resolveDemoLang(): DemoVideoLang {
        const current = (this.translate.currentLang || 'fr').toLowerCase();
        if (current.startsWith('fr')) return 'fr';
        if (current.startsWith('es')) return 'es';
        if (current.startsWith('it')) return 'it';
        return 'en';
    }

    /** Met à jour l'URL iframe uniquement quand la langue d'interface ou la marque change. */
    private refreshDemoPage(): void {
        this.stopDemoPlayback();
        const lang = this.resolveDemoLang();
        const brand = this.brand === 'lilate' ? 'lilate' : 'flashlevel';
        const assetPath = `${DEMO_HTML_PLAYER}?lang=${lang}&brand=${brand}&autostart=0`;
        this.demoPageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(assetPath);
        const titles: Record<OnboardingBrand, Record<DemoVideoLang, string>> = {
            flashlevel: {
                fr: 'Démo interface FlashLevel',
                en: 'FlashLevel interface demo',
                es: 'Demostración de la interfaz FlashLevel',
                it: 'Demo interfaccia FlashLevel',
            },
            lilate: {
                fr: 'Démo interface LILATE',
                en: 'LILATE interface demo',
                es: 'Demostración de la interfaz LILATE',
                it: 'Demo interfaccia LILATE',
            },
        };
        this.demoPageTitle = titles[brand][lang];
        this.playbackStarted = false;
        this.cdr.markForCheck();
    }

    /** Lance la lecture depuis un geste utilisateur (clic CGU) — requis par Chrome pour le TTS. */
    startPlaybackFromGesture(): void {
        const invoke = (): boolean => {
            const frame = this.demoFrame?.nativeElement;
            const win = frame?.contentWindow as (Window & { flashlevelStartDemo?: () => void }) | null;
            if (!win) return false;
            if (typeof win.flashlevelStartDemo === 'function') {
                win.flashlevelStartDemo();
            } else {
                win.postMessage({ type: 'flashlevel:start' }, '*');
            }
            return true;
        };
        if (invoke()) {
            this.playbackStarted = true;
            return;
        }
        this.playbackStarted = false;
        this.startDemoPlayback();
    }

    /** Déclenche la lecture dans l'iframe (fallback si le warmup n'était pas prêt au clic CGU). */
    private startDemoPlayback(): void {
        if (this.playbackStarted) {
            return;
        }
        const attemptId = ++this.startAttemptId;
        const tryStart = (attempt = 0): void => {
            if (attemptId !== this.startAttemptId) {
                return;
            }
            const frame = this.demoFrame?.nativeElement;
            if (frame?.contentWindow) {
                this.playbackStarted = true;
                frame.contentWindow.postMessage({ type: 'flashlevel:start' }, '*');
                return;
            }
            if (attempt < 30) {
                setTimeout(() => tryStart(attempt + 1), 100);
            }
        };
        tryStart();
    }

    /** Coupe la voix off et la timeline dans l'iframe (changement d'étape onboarding). */
    stopDemoPlayback(): void {
        this.startAttemptId++;
        this.playbackStarted = false;
        const frame = this.demoFrame?.nativeElement;
        const win = frame?.contentWindow as (Window & { flashlevelStopDemo?: () => void }) | null;
        if (!win) {
            return;
        }
        if (typeof win.flashlevelStopDemo === 'function') {
            win.flashlevelStopDemo();
        } else {
            win.postMessage({ type: 'flashlevel:stop' }, '*');
        }
    }

    /** Relance le démarrage si l'iframe finit de charger après le passage visible. */
    onIframeLoad(): void {
        if (this.visible) {
            this.startDemoPlayback();
        }
    }
}
